// The order's physical size for upload exports, read from the dimension option linked to the upload-lift option
// (`optionDimension`). The upload field (upload-lift) takes it as `field.updateConfig({ exportSize: { w, h, u } })` and
// adds it to the export tokens of entries whose published pipeline uses the order size; without it (null) the
// pipeline's own default size applies. The unit is the upload-lift option's existing `dimensionUnit` - the same unit
// the dimension values are filled in from the image - never the dimension's free-text addon labels.

/** upload-lift `dimensionUnit` → the export unit and the factor its values are multiplied with. */
const DIMENSION_UNITS = {
    mm: ['mm', 1],
    cm: ['cm', 1],
    m: ['cm', 100],
    inch: ['in', 1],
    feet: ['in', 12],
};

const positive = value => Number.isFinite(value) && value > 0;

// exact decimal scaling (m → cm, feet → in) without binary noise such as 1.1 * 100 = 110.00000000000001
const scale = (value, factor) => factor === 1 ? value : Number((value * factor).toPrecision(12));

/**
 * The export size of a dimension option in the upload-lift option's `dimensionUnit`, or null when it has none:
 * px / no unit, a hidden option (conditions), a missing or non-positive side, or values outside the merchant's
 * min/max limits. A width-only option (`onlyX`) takes its height from the fixed `ratio` (x / y), else from a fixed
 * `valueY`, else it has no size.
 */
export function dimensionExportSize(dimensionOption, dimensionUnit) {
    const unit = DIMENSION_UNITS[dimensionUnit];
    if (!unit || !dimensionOption || !dimensionOption.params) return null;
    // hidden by its conditions: the order carries no size, the config default applies
    if (dimensionOption.visible === false) return null;
    const params = dimensionOption.params;
    const controller = dimensionOption._controller;
    if (!controller) return null;
    const w = Number(controller.x);
    let h = Number(controller.y);
    if (params.onlyX) {
        // Number(null) and Number('') are 0: an unset ratio / valueY is never positive
        const ratio = Number(params.ratio);
        const valueY = Number(params.valueY);
        h = positive(ratio) ? w / ratio : positive(valueY) ? valueY : NaN;
    }
    if (!positive(w) || !positive(h)) return null;
    // the option clears its errors before announcing a change: re-check the merchant's limits here
    if (typeof controller.validateDimension === 'function') {
        const errors = [];
        controller.validateDimension(dimensionOption, errors);
        if (errors.length) return null;
    }
    return { w: scale(w, unit[1]), h: scale(h, unit[1]), u: unit[0] };
}

/** Equal sizes (both null, or the same w, h and unit) - avoids rebuilding the upload inputs on every change. */
export function sameExportSize(a, b) {
    if (!a || !b) return !a && !b;
    return a.w === b.w && a.h === b.h && a.u === b.u;
}
